import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";
import { useLogLogin } from "@/hooks/useLoginHistory";
import { useMFA } from "@/hooks/useMFA";
import { toast } from "sonner";
import { AppLogo } from "@/components/branding/AppLogo";

const Login = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const logLogin = useLogLogin();
  const { getAAL, factors } = useMFA();
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    const { error } = await signIn(email, password);
    
    if (error) {
      setIsLoading(false);
      toast.error("Email ou senha inválidos");
      return;
    }

    // Log the login attempt
    try {
      await logLogin.mutateAsync();
    } catch (e) {
      console.error("Error logging login:", e);
    }

    // Check if MFA is required
    try {
      const aal = await getAAL();
      if (aal?.nextLevel === "aal2" && aal?.currentLevel === "aal1") {
        setIsLoading(false);
        navigate("/verify-2fa", { state: { redirectTo: "/dashboard" } });
        return;
      }
    } catch (e) {
      console.error("Error checking MFA:", e);
    }

    setIsLoading(false);
    navigate("/dashboard");
  };

  return (
    <main className="flex min-h-screen">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between gradient-navy p-12">
        <AppLogo size="lg" showSubtitle />

        <div className="space-y-6">
          <h2 className="text-4xl font-bold text-sidebar-foreground leading-tight">
            Gestão jurídica e consultiva de excelência
          </h2>
          <p className="text-lg text-sidebar-foreground/70 max-w-md">
            Gerencie processos judiciais e cases de consultoria em uma única plataforma integrada, 
            com automações inteligentes e conformidade DataJud.
          </p>
          <div className="flex gap-8 pt-4">
            <div>
              <p className="text-3xl font-bold text-sidebar-primary">500+</p>
              <p className="text-sm text-sidebar-foreground/60">Processos gerenciados</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-sidebar-primary">98%</p>
              <p className="text-sm text-sidebar-foreground/60">Satisfação dos clientes</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-sidebar-primary">24/7</p>
              <p className="text-sm text-sidebar-foreground/60">Monitoramento ativo</p>
            </div>
          </div>
        </div>

        <p className="text-sm text-sidebar-foreground/40">
          © 2024 NORIX Global. Todos os direitos reservados.
        </p>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex w-full lg:w-1/2 items-center justify-center bg-background p-8">
        <Card className="w-full max-w-md border-border/50 shadow-lg">
          <CardHeader className="space-y-1 text-center">
            {/* Mobile Logo */}
            <div className="flex lg:hidden items-center justify-center mb-4">
              <AppLogo size="md" />
            </div>
            
            <CardTitle className="text-2xl font-bold text-foreground">
              Bem-vindo de volta
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              Entre com suas credenciais para acessar o sistema
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-foreground">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-foreground">Senha</Label>
                  <Link 
                    to="/forgot-password" 
                    className="text-xs text-muted-foreground hover:text-primary transition-colors"
                  >
                    Esqueceu a senha?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground min-h-6 min-w-6 flex items-center justify-center"
                    aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                variant="gold"
                size="lg"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? "Entrando..." : "Entrar"}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-muted-foreground">
              <p>Ainda não tem conta?</p>
              <Link to="/cadastro" className="text-primary hover:underline font-medium">
                Criar conta
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </main>
  );
};

export default Login;
